// Theme: a plain light/dark toggle (no "auto"). Persisted to localStorage and
// applied as `data-theme` on <html>; the CSS sets `color-scheme` per theme so
// native controls (scrollbars, inputs) match. A pre-paint script in index.html
// applies the saved/system value before React mounts to avoid a flash.
import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark';

const THEME_KEY = 'mv_theme';

/** Saved theme, or the system preference on first visit. */
function read(): ThemeMode {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    // ignore
  }
  const prefersDark =
    typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
}

/** Glyph shown on the toggle for the current mode. */
export function themeGlyph(mode: ThemeMode): string {
  return mode === 'dark' ? '☾' : '☀';
}

export function useTheme(): { mode: ThemeMode; toggle: () => void } {
  const [mode, setMode] = useState<ThemeMode>(read);

  // Apply after commit so the DOM always matches the committed state.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    try {
      localStorage.setItem(THEME_KEY, mode);
    } catch {
      // ignore
    }
  }, [mode]);

  const toggle = useCallback(() => {
    setMode((cur) => (cur === 'dark' ? 'light' : 'dark'));
  }, []);

  return { mode, toggle };
}
